import { authApi } from '../features/auth/authApi';
import { companyApi } from '../services/companyApi';
import { presentacionesApi } from '../services/presentacionesApi';
import { configureStore, ThunkAction, Action, Middleware } from '@reduxjs/toolkit';
import { createStateSyncMiddleware, initMessageListener } from 'redux-state-sync';
import { createUsuarios } from '../services/CreateUser';
import { newsApi, newsGestorApi } from '../services/newsApi';
import { notificationsApi } from '../services/notificationsApi';
import { persistStore, persistReducer, PERSIST, PURGE, REHYDRATE } from 'redux-persist';
import { usersApi } from '../services/usersApi';
import applicationSlice from '../features/slices/applicationSlice';
import authReducer, { logout } from '../features/auth/authSlice';
import storage from 'redux-persist/lib/storage';
import { rolesApi } from '../services/rolesApi';
import themeReducer from './slices/themeSlice';
import { presentacionesRarApi } from '../services/presentacionesRarApi';
import filesReducer from './slices/filesSlice';
import responsableReducer from './slices/responsableSlice';
import massiveUploadReducer from './slices/massiveUploadSlice';
import rarPresentacionReducer from './slices/rarPresentacionSlice';
import datosParametricosReducer from './slices/datosParametricosSlice';
import establishmentReducer from './slices/establishmentSlice';
import gremioContratistaReducer from './slices/gremioContratistaSlice';
import srtPresentationsReducer from './slices/srtPresentationsSlice';
import carcinogenicReportReducer from './slices/carcinogenicReportSlice';
import presentationsBOReducer from './slices/presentationsSliceBO';
import visitsReducer from './slices/visitsSlice';
import { organismsApi } from '../services/organismsApi';
import { employeesApi } from '../services/employeesApi';
import { helpCenterApi } from '../services/helpCenterApi';
import { workPositionsApi } from '../services/workPositionsApi';
import { nominaApi } from '../services/nominaApi';
import { rgrlVinculationApi } from '../services/rgrlVinculationApi';
import { carcinogenicApi } from '../services/carcinogenicApi';
import contractReducer from './slices/contractSlice';
import { visitasApi } from '../services/visitasApi';
import { periodicosApi } from '../services/periodicosApi';
import organizationsReducer from './slices/organizationsSlice';
import { organizationsApi } from '../services/organizationsApi';
import { avisoObraApi } from '../services/avisoObraApi';
import { contractsApi } from '../services/contratctsApi';
import { providersApi } from '../services/providersApi';
import { auditoriaFacturacionApi } from '../services/auditoriaFacturacionApi';
import periodicosSliceReducer from './slices/periodicosSlice';
import { roamApi } from '../services/roamApi';
import { cargaMasivaApi } from '../services/cargaMasivaApi';
import { periodicosEmployeeApi } from '../services/periodicosEmployeeApi';
import { asistenteIAApi } from '../services/asistenteIAApi';

const persistConfig = {
    key: 'root',
    storage
};

const authPersistConfig = {
    key: 'auth',
    storage
};

const establishmentPersistConfig = {
    key: 'establishment',
    storage
};

const srtPresentationsPersistConfig = {
    key: 'srtPresentations',
    storage
};

const carcinogenicReportPersistConfig = {
    key: 'carcinogenicReport',
    storage
};

const organizationsPersistConfig = {
    key: 'organizations',
    storage
};

const visitsPersistConfig = {
    key: 'visits',
    storage
};

const presentationsBOPersistConfig = {
    key: 'presentationsBO',
    storage
};

const periodicosSlicePersistConfig = {
    key: 'periodicos',
    storage
};


const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);
const persistedEstablishmentReducer = persistReducer(
    establishmentPersistConfig,
    establishmentReducer
);
const persistSRTPresentationsReducer = persistReducer(
    srtPresentationsPersistConfig,
    srtPresentationsReducer
);

const persistCarcinogenicReportReducer = persistReducer(
    carcinogenicReportPersistConfig,
    carcinogenicReportReducer
);

const persistOrganizationsReducer = persistReducer(
    organizationsPersistConfig,
    organizationsReducer
);

const persistedPresentationsBOReducer = persistReducer(
    presentationsBOPersistConfig,
    presentationsBOReducer
);

const persistedPeriodicosReducer = persistReducer(
    periodicosSlicePersistConfig,
    periodicosSliceReducer
);

const persistVisitsReducer = persistReducer(visitsPersistConfig, visitsReducer);

// usersApi cachea datos del usuario logueado (firma, datos de perfil, puertos) en queries sin
// argumentos (getUserFirma, getUserData, getUserPorts), por lo que la key de cache de RTK Query
// es la misma para cualquier usuario. Al cerrar sesion y loguearse con otro usuario en la misma
// pestaña (sin recargar la pagina) esos datos quedaban pisados con los del usuario anterior,
// por ejemplo la firma del preventor. Se limpia el cache de usersApi apenas se dispara el logout.
const resetUsersApiCacheOnLogout: Middleware = (storeApi) => (next) => (action) => {
    const result = next(action);

    if (action.type === logout.type) {
        storeApi.dispatch(usersApi.util.resetApiState());
    }

    return result;
};

export const store = configureStore({
    reducer: {
        [authApi.reducerPath]: authApi.reducer,
        [createUsuarios.reducerPath]: createUsuarios.reducer,
        [usersApi.reducerPath]: usersApi.reducer,
        empresa: companyApi.reducer,
        auth: persistedAuthReducer,
        theme: themeReducer,
        application: applicationSlice,
        [notificationsApi.reducerPath]: notificationsApi.reducer,
        [newsApi.reducerPath]: newsApi.reducer,
        [newsGestorApi.reducerPath]: newsGestorApi.reducer,
        roles: rolesApi.reducer,
        [presentacionesRarApi.reducerPath]: presentacionesRarApi.reducer,
        files: filesReducer,
        [presentacionesApi.reducerPath]: presentacionesApi.reducer,
        [organismsApi.reducerPath]: organismsApi.reducer,
        [nominaApi.reducerPath]: nominaApi.reducer,
        [employeesApi.reducerPath]: employeesApi.reducer,
        [helpCenterApi.reducerPath]: helpCenterApi.reducer,
        [workPositionsApi.reducerPath]: workPositionsApi.reducer,
        [contractsApi.reducerPath]: contractsApi.reducer,
        [providersApi.reducerPath]: providersApi.reducer,
        [auditoriaFacturacionApi.reducerPath]: auditoriaFacturacionApi.reducer,
        [rgrlVinculationApi.reducerPath]: rgrlVinculationApi.reducer,
        [carcinogenicApi.reducerPath]: carcinogenicApi.reducer,
        responsable: responsableReducer,
        rarPresentacion: rarPresentacionReducer,
        datosParametricos: datosParametricosReducer,
        establishment: persistedEstablishmentReducer,
        gremioContratista: gremioContratistaReducer,
        srtPresentations: persistSRTPresentationsReducer,
        carcinogenicReport: persistCarcinogenicReportReducer,
        visits: persistVisitsReducer,
        presentationsBO: persistedPresentationsBOReducer,
        periodicos: persistedPeriodicosReducer,
        massiveUpload: massiveUploadReducer,
        contract: contractReducer,
        [visitasApi.reducerPath]: visitasApi.reducer,
        [periodicosApi.reducerPath]: periodicosApi.reducer,
        [periodicosEmployeeApi.reducerPath]: periodicosEmployeeApi.reducer,
        [avisoObraApi.reducerPath]: avisoObraApi.reducer,
        [organizationsApi.reducerPath]: organizationsApi.reducer,
        [roamApi.reducerPath]: roamApi.reducer,
        [cargaMasivaApi.reducerPath]: cargaMasivaApi.reducer,
        [asistenteIAApi.reducerPath]: asistenteIAApi.reducer,
        organizations: organizationsReducer
    },
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: false,
            immutableCheck: { warnAfter: 128 }
        })
            .concat(authApi.middleware)
            .concat(createUsuarios.middleware)
            .concat(usersApi.middleware)
            .concat(companyApi.middleware)
            .concat(notificationsApi.middleware)
            .concat(newsApi.middleware)
            .concat(newsGestorApi.middleware)
            .concat(rolesApi.middleware)
            .concat(presentacionesRarApi.middleware)
            .concat(presentacionesApi.middleware)
            .concat(organismsApi.middleware)
            .concat(nominaApi.middleware)
            .concat(employeesApi.middleware)
            .concat(helpCenterApi.middleware)
            .concat(workPositionsApi.middleware)
            .concat(contractsApi.middleware)
            .concat(providersApi.middleware)
            .concat(auditoriaFacturacionApi.middleware)
            .concat(rgrlVinculationApi.middleware)
            .concat(visitasApi.middleware)
            .concat(carcinogenicApi.middleware)
            .concat(periodicosApi.middleware)
            .concat(periodicosEmployeeApi.middleware)
            .concat(organizationsApi.middleware)
            .concat(avisoObraApi.middleware)
            .concat(roamApi.middleware)
            .concat(cargaMasivaApi.middleware)
            .concat(asistenteIAApi.middleware)
            .concat(resetUsersApiCacheOnLogout)
            .concat(
                createStateSyncMiddleware({
                    blacklist: [PURGE, PERSIST, REHYDRATE],
                    broadcastChannelOption: { type: 'localstorage' }
                })
            )
});

initMessageListener(store);
export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
    ReturnType,
    RootState,
    unknown,
    Action<string>
>;

